import React, {useState} from 'react'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import * as XLSX from 'xlsx'
import {Product} from '../core/OrderedProductsModel'

interface ExportButtonProps {
  products: Product[]
  disabled?: boolean
}

const ExportButton: React.FC<ExportButtonProps> = ({products, disabled}) => {
  const {formatMessage} = useIntl()
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    setIsExporting(true)
    try {
      // Prepare data for Excel
      const data = products.map(product => ({
        [formatMessage({id: 'product'})]: product.product_name,
        [formatMessage({id: 'ORDERED_PRODUCTS.BARCODE'})]: product.barcode,
        [formatMessage({id: 'ORDERED_PRODUCTS.MODEL'})]: product.model,
        [formatMessage({id: 'ORDERED_PRODUCTS.QUANTITY'})]: Number(product.quantity),
        [formatMessage({id: 'ORDERED_PRODUCTS.STATUS'})]: formatMessage({
          id: `ORDERED_PRODUCTS.STATUS.${product.item_status.toUpperCase()}`,
          defaultMessage: product.item_status
        })
      }))

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(data)

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Ordered Products')

      // Generate Excel file with localized filename
      const filename = formatMessage({id: 'ORDERED_PRODUCTS.EXCEL_FILENAME'})
      XLSX.writeFile(wb, `${filename}-${new Date().toISOString()}.xlsx`)
      
      toast.success(formatMessage({id: 'ORDERED_PRODUCTS.EXPORT_SUCCESS'}))
    } catch (error: any) {
      console.error('Error exporting products:', error)
      toast.error(formatMessage({id: 'ORDERED_PRODUCTS.EXPORT_ERROR'}))
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <button
      className='btn btn-sm btn-dark'
      onClick={handleExport}
      disabled={isExporting || disabled}
    >
      {isExporting ? (
        <>
          <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
          {formatMessage({id: 'ORDERED_PRODUCTS.EXPORTING'})}
        </>
      ) : (
        formatMessage({id: 'ORDERED_PRODUCTS.EXPORT_TO_EXCEL'})
      )}
    </button>
  )
}

export default ExportButton
